import React, { useCallback, useEffect, useRef, useState, type FC } from "react";
import "./SearchObject.scss";
import { Input } from "@shared/ui/input";
import { Select } from "@shared/ui/select";
import { FormFooter } from "@shared/ui/form-footer";
import { FormPanel } from "@shared/ui/form-panel";
import { BusinessForm, type BusinessFormRenderProps, type FormHandle } from "@src/components/business-form";
import { soApi } from "@src/api/so";
import { strIsEmpty } from "../../helpers/text-services/stringEmpty";
import { reportError } from "@app/services/error-reporter";
import { useNavigate } from "react-router-dom";
import BarcodeScanner from "../../components/service/BarcodeScanner/barcodeScanner";
import type { ScannerHintConfig } from "@app/scanner";
import { buildSearchObjectVariants, type SearchObjectNavigationVariant } from "./buildSearchObjectVariants";
import { searchObjectFormConfig, type SearchObjectFormValues } from "./searchObject.form-config";

const SEARCH_OBJECT_SCAN_HINT: ScannerHintConfig = {
    title: "Сканируйте номер объекта (СО)",
    description: "Найдём доступные варианты перехода к объекту.",
    position: { offset: { x: 0, y: -50 } },
};

const SEARCH_DEBOUNCE_MS = 1000;

const SEARCH_OBJECT_INITIAL_VALUES: SearchObjectFormValues = {
    objNum: "",
    variantName: "",
};

async function resolveSearchObjectVariants(
    objNum: string,
): Promise<{ ok: true; variants: SearchObjectNavigationVariant[] } | { ok: false; message: string }> {
    const result = await soApi.findObject({ soNum: objNum });
    if (result.soCode === 0 || result.soType === 0) {
        return {
            ok: false,
            message: `По заданному номеру СО "${objNum}" не найден объект системы.`,
        };
    }

    const variants = await buildSearchObjectVariants(result.soType, result.soCode);
    if (variants.length === 0) {
        return {
            ok: false,
            message: `Не найдено вариантов для навигации для СО с типом "${result.soType}" и кодом "${result.soCode}"`,
        };
    }

    return { ok: true, variants };
}

/** Синхронизирует `objNum` из формы в родителя (hooks нельзя в render-prop FormPanel). */
const SearchObjectObjNumWatcher: FC<{
    form: BusinessFormRenderProps<SearchObjectFormValues>;
    onObjNumChange: (objNum: string) => void;
}> = ({ form, onObjNumChange }) => {
    const objNum = form.watch("objNum");

    useEffect(() => {
        onObjNumChange(objNum);
    }, [objNum, onObjNumChange]);

    return null;
};

/** Пропсы content-формы поиска СО (fullscreen overlay). */
export type SearchObjectFormProps = Record<string, never>;

/**
 * Форма поиска СО и перехода к объекту. \
 * Открывается как fullscreen window из нижней навигации.
 */
export const SearchObjectForm: FC<SearchObjectFormProps> = () => {
    const navigate = useNavigate();
    const formApiRef = useRef<FormHandle<SearchObjectFormValues> | null>(null);
    const formRenderRef = useRef<BusinessFormRenderProps<SearchObjectFormValues> | null>(null);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const searchRequestIdRef = useRef(0);
    const skipWatchSearchRef = useRef(false);

    const [variants, setVariants] = useState<SearchObjectNavigationVariant[] | null>(null);

    const applySearchResult = useCallback(async (objNum: string): Promise<void> => {
        const form = formRenderRef.current;
        if (form == null) return;

        form.clearErrors("objNum");

        if (strIsEmpty(objNum)) {
            setVariants(null);
            form.clearErrors("variantName");
            if (form.getValues("variantName") !== "") {
                form.setValue("variantName", "", { shouldValidate: false });
            }
            return;
        }

        const requestId = ++searchRequestIdRef.current;

        try {
            const result = await resolveSearchObjectVariants(objNum);
            if (requestId !== searchRequestIdRef.current) return;

            if (!result.ok) {
                setVariants(null);
                form.clearErrors("variantName");
                form.setValue("variantName", "", { shouldValidate: false });
                form.setError("objNum", { type: "manual", message: result.message });
                return;
            }

            setVariants(result.variants);
            form.setValue("variantName", result.variants[0]?.name ?? "", { shouldValidate: true });
        } catch (error) {
            if (requestId !== searchRequestIdRef.current) return;
            reportError(error, { from: "SearchObject.search" });
        }
    }, []);

    const scheduleSearch = useCallback(
        (objNum: string) => {
            if (searchTimerRef.current != null) {
                clearTimeout(searchTimerRef.current);
                searchTimerRef.current = null;
            }

            if (strIsEmpty(objNum)) {
                searchRequestIdRef.current += 1;
                void applySearchResult(objNum);
                return;
            }

            searchTimerRef.current = setTimeout(() => {
                searchTimerRef.current = null;
                void applySearchResult(objNum);
            }, SEARCH_DEBOUNCE_MS);
        },
        [applySearchResult],
    );

    const isFirstObjNumWatchRef = useRef(true);

    const handleObjNumChange = useCallback(
        (objNum: string) => {
            if (skipWatchSearchRef.current) {
                skipWatchSearchRef.current = false;
                return;
            }
            if (isFirstObjNumWatchRef.current) {
                isFirstObjNumWatchRef.current = false;
                if (strIsEmpty(objNum)) return;
            }
            scheduleSearch(objNum);
        },
        [scheduleSearch],
    );

    const handleSubmit = (values: SearchObjectFormValues): void => {
        const selected = variants?.find((item) => item.name === values.variantName);
        if (selected == null) return;
        navigate(selected.path);
    };

    const handleScan = async (code: string): Promise<void> => {
        const form = formRenderRef.current;
        if (form == null) return;

        if (searchTimerRef.current != null) {
            clearTimeout(searchTimerRef.current);
            searchTimerRef.current = null;
        }

        skipWatchSearchRef.current = true;
        form.setValue("objNum", code, { shouldDirty: true, shouldValidate: true });
        await applySearchResult(code);
    };

    useEffect(() => {
        return () => {
            if (searchTimerRef.current != null) {
                clearTimeout(searchTimerRef.current);
            }
        };
    }, []);

    return (
        <div className="search_object">
            <FormPanel<SearchObjectFormValues>
                className="search_object__panel"
                contentClassName="search_object__panel_body"
                formApiRef={formApiRef}
                formProps={{
                    formConfig: searchObjectFormConfig,
                    initialValues: SEARCH_OBJECT_INITIAL_VALUES,
                    onSubmit: handleSubmit,
                }}
                canSubmit={variants != null && variants.length > 0}
                footer={({ submitForm, onRequestDismiss }) => (
                    <FormFooter
                        confirmText="Перейти"
                        cancelText="Закрыть"
                        onConfirm={submitForm}
                        onCancel={onRequestDismiss}
                        confirmDisabled={variants == null || variants.length === 0}
                    />
                )}
            >
                {(form) => {
                    formRenderRef.current = form;
                    const variantField = form.field("variantName").getControlledPropsWithLabel();

                    return (
                        <>
                            <SearchObjectObjNumWatcher form={form} onObjNumChange={handleObjNumChange} />
                            <p className="search_object__description">
                                Сканируйте штрихкод с номером объекта или введите его вручную.
                            </p>
                            <BusinessForm.Field name="objNum">
                                <Input {...form.field("objNum").getPropsWithLabel()} spellCheck={false} />
                            </BusinessForm.Field>
                            <BusinessForm.Field name="variantName">
                                <Select
                                    {...variantField}
                                    value={variantField.value === "" ? null : variantField.value}
                                    onChange={(name) => {
                                        variantField.onChange(name ?? "");
                                    }}
                                    options={variants ?? []}
                                    getOptionData={(option) => ({
                                        value: option.name,
                                        label: option.name,
                                    })}
                                    disabled={variants == null || variants.length <= 1}
                                />
                            </BusinessForm.Field>
                        </>
                    );
                }}
            </FormPanel>
            <BarcodeScanner
                hint={SEARCH_OBJECT_SCAN_HINT}
                captureInEditable
                errorSource="SearchObject.scan"
                onScan={handleScan}
            />
        </div>
    );
};
